import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import InvoicesListViewData from './invoicesListViewData';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    date: 250,
    invoice_number: 250,
    company: 250,
    machines: 250,
    amount: 250,
    documents: 250,
};

const InvoicesListView = React.memo(({ companies, invoiceFilters, setInvoicefilters, sortBy, setSortBy, scrollRef, invoices, setOffset, filteredInvoicesCount, setInvoicesFetched }) => {

    const navigate = useNavigate();

    const [invoiceColumnWidth, setInvoiceColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('invoiceColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredInvoicesCount, setInvoicesFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setInvoicesFetched, filteredInvoicesCount, 70, 280);

    const handleInvoiceClick = useCallback((id) => {
        navigate(`/dashboard/factures/facture_details/${id}`);
    }, [navigate]);

    const handleColumnWidthChange = (value, columnKey) => {
        setInvoiceColumnWidth({ ...invoiceColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('invoiceColumnWidth', JSON.stringify(invoiceColumnWidth));
    }, [invoiceColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { invoices, handleInvoiceClick } = data;
        const invoice = index === 0 ? invoices[0] : invoices[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <InvoicesListViewData
                    invoice={invoice}
                    columnWidth={invoiceColumnWidth}
                    handleInvoiceClick={handleInvoiceClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table' sx={{ width: '98% !important', margin: '0 auto' }}>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Date'
                    columnKey='date'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFixed={true}
                    isFilter={true}
                    value={invoiceFilters.date}
                    onChange={(e) => { setInvoicefilters({ ...invoiceFilters, date: e }) }}
                    options='date'
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Numéro de la facture'
                    columnKey='invoice_number'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFixed={false}
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Entreprise associé'
                    columnKey='company'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={invoiceFilters.company}
                    onChange={(e) => setInvoicefilters({ ...invoiceFilters, company: e.target.value })}
                    options={companies}
                    isFixed={false}
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Machines concernées'
                    columnKey='machines'
                    isFixed={false}
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Montant'
                    columnKey='amount'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={invoiceFilters.amount}
                    onChange={(e) => { setInvoicefilters({ ...invoiceFilters, amount: e }) }}
                    options='number'
                    isFixed={false}
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Documents'
                    columnKey='documents'
                    isFixed={false}
                    columnWidth={invoiceColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell width="50px" backgroundColor='#F6F6F6' />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredInvoicesCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredInvoicesCount}
                    itemSize={70}
                    itemData={{ invoices, handleInvoiceClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default InvoicesListView;